'use client'

import React from 'react'
import { Area, AreaChart, Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { apiGet } from './entriesApi'
import type { AutoDomainScreenPass, AutoDomainSummary } from './autoDomainTypes'

// The chart cards of the Domain Candidates dashboard. Same chrome and
// chart language as the SIBS mail dashboard (LegitimateFraudulentWaveChart /
// charts/ProtectionLatencyCard): rounded card, eyebrow + bold title,
// dot legend in the header, gradient area for a timeline, rounded columns for
// a distribution, the shared monospace tooltip.

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const fmtMonthTick = (ym: string) => {
  const [y, m] = ym.split('-')
  if (!m) return ym
  return m === '01' ? y : `${MONTHS_SHORT[Number(m) - 1]} ${y.slice(2)}`
}
const fmtMonthLong = (ym: string) => {
  const [y, m] = ym.split('-')
  return m ? `${MONTHS_SHORT[Number(m) - 1]} ${y}` : ym
}

const REGISTRATION_WINDOW_MONTHS = 24
const LURE = 'var(--destructive)'
const OTHER = 'var(--signal)'

function cardCls(isDark: boolean) {
  return `relative flex min-w-0 flex-col overflow-hidden border border-border bg-card`
}
function tooltipCls(isDark: boolean) {
  return `rounded border px-3 py-2 text-[11px] font-mono shadow-notice border-border bg-card text-muted-foreground`
}
const tick = (isDark: boolean) => ({ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' })

export function RegistrationWaveCard({
  summary,
  isDark,
  muted,
  text,
  className = 'h-full',
}: {
  summary: AutoDomainSummary
  isDark: boolean
  muted: string
  text: string
  className?: string
}) {
  // Legit infrastructure the sourcing brushes against (a 1995 zendesk, a
  // deloitte host) would stretch the axis over three decades and flatten the
  // part that matters, so the plot keeps the last two years and the subtitle
  // carries the rest as a number.
  const all = summary.registration_histogram
  const data = all.slice(-REGISTRATION_WINDOW_MONTHS)
  const earlier = all.slice(0, Math.max(0, all.length - REGISTRATION_WINDOW_MONTHS)).reduce((n, d) => n + d.count, 0)
  const total = all.reduce((n, d) => n + d.count, 0)
  const lures = all.reduce((n, d) => n + d.lure, 0)
  const bg = 'var(--card)'
  const tickInterval = Math.max(0, Math.ceil(data.length / 8) - 1)
  const span = data.length > 0 ? `${fmtMonthLong(data[0].month)} – ${fmtMonthLong(data[data.length - 1].month)}` : 'No WHOIS dates yet'
  const omitted = summary.registration_unregistered + summary.registration_unknown

  return (
    <div className={`${cardCls(isDark)} ${className}`}>
      <div className="flex shrink-0 items-start justify-between gap-4 px-4 pt-3 pb-2">
        <div className="min-w-0">
          <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Registrations by month · WHOIS created</div>
          <div className={`mt-0.5 text-base font-semibold tracking-tight ${text}`}>{span}</div>
          <div className={`mt-0.5 font-mono text-[10px] ${muted}`}>
            {total.toLocaleString()} registered · <span className={'text-destructive'}>{lures}</span> brand impersonations
            {earlier > 0 && <> · {earlier} before {fmtMonthLong(data[0].month)}</>}
            {omitted > 0 && <> · {summary.registration_unregistered} unregistered, {summary.registration_unknown} without a date (not plotted)</>}
          </div>
        </div>
        <div className={`flex min-w-0 shrink items-center justify-end gap-3 font-mono text-[9px] ${muted}`}>
          <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-signal" />Other</span>
          <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-destructive" />Impersonation</span>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {data.length === 0 ? (
          <div className={`flex h-full items-center justify-center font-mono text-[11px] ${muted}`}>No registration dates in this scope</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 8, left: -24, bottom: 4 }}>
              <defs>
                <linearGradient id="adRegOther" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={OTHER} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={OTHER} stopOpacity={0.06} />
                </linearGradient>
                <linearGradient id="adRegLure" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={LURE} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={LURE} stopOpacity={0.06} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" tick={tick(isDark)} tickLine={false} axisLine={false} interval={tickInterval} padding={{ left: 16, right: 8 }} tickFormatter={fmtMonthTick} />
              <YAxis tick={tick(isDark)} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={({ active, payload, label }: any) => {
                if (!active || !payload?.length) return null
                const row = payload[0].payload as { count: number; lure: number; other: number }
                return (
                  <div className={tooltipCls(isDark)}>
                    <div className={`mb-1 font-semibold ${muted}`}>{fmtMonthLong(String(label))}</div>
                    <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full" style={{ background: OTHER }} /><span>Other</span><span className={`ml-auto pl-4 font-semibold ${text}`}>{row.other}</span></div>
                    <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full" style={{ background: LURE }} /><span>Impersonation</span><span className={`ml-auto pl-4 font-semibold ${text}`}>{row.lure}</span></div>
                    <div className={`mt-1 flex items-center justify-between gap-6 ${muted}`}><span>Registered</span><span className={`font-semibold ${text}`}>{row.count}</span></div>
                  </div>
                )
              }} />
              <Area type="monotone" dataKey="other" stroke={OTHER} strokeWidth={1.5} fill="url(#adRegOther)" dot={false}
                activeDot={(p: any) => p.value > 0 ? <circle key={p.index} cx={p.cx} cy={p.cy} r={4} fill={OTHER} stroke={bg} strokeWidth={1.5} /> : <g key={p.index} />} isAnimationActive={false} />
              <Area type="monotone" dataKey="lure" stroke={LURE} strokeWidth={1.5} fill="url(#adRegLure)" dot={false}
                activeDot={(p: any) => p.value > 0 ? <circle key={p.index} cx={p.cx} cy={p.cy} r={4} fill={LURE} stroke={bg} strokeWidth={1.5} /> : <g key={p.index} />} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

export function QualityDistributionCard({
  summary,
  isDark,
  muted,
  text,
  className = 'h-full',
}: {
  summary: AutoDomainSummary
  isDark: boolean
  muted: string
  text: string
  className?: string
}) {
  const data = summary.quality_histogram.map((b) => ({ ...b, label: b.bin.toFixed(2) }))
  const total = data.reduce((n, d) => n + d.count, 0)
  const strong = data.filter((d) => d.bin >= 0.75).reduce((n, d) => n + d.count, 0)
  const strongPct = total > 0 ? Math.round((strong / total) * 100) : 0

  return (
    <div className={`${cardCls(isDark)} ${className}`}>
      <div className="flex shrink-0 flex-col gap-0.5 px-4 pt-3 pb-2">
        <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Sourcing quality distribution</div>
        {total > 0 && (
          <div className={`font-mono text-[10px] ${muted}`}>
            <span className={'text-signal'}>{strongPct}%</span> scored 0.75 or higher
            {summary.quality_unscored > 0 && <> · {summary.quality_unscored} unscored</>}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 12, right: 4, left: -30, bottom: 4 }} barCategoryGap="20%">
            <XAxis dataKey="label" tick={tick(isDark)} tickLine={false} axisLine={false} interval={4} />
            <YAxis tick={tick(isDark)} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'var(--secondary)' }} content={({ active, payload }: any) => {
              if (!active || !payload?.length) return null
              const p = payload[0].payload as { bin: number; count: number }
              return (
                <div className={tooltipCls(isDark)}>
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: OTHER }} />
                    <span>quality {p.bin.toFixed(2)} – {(p.bin + 0.05).toFixed(2)}</span>
                    <span className={`ml-auto pl-4 font-semibold ${text}`}>{p.count}</span>
                  </div>
                  <div className={`mt-1 flex items-center justify-between gap-6 ${muted}`}>
                    <span>Share</span>
                    <span className={`font-semibold ${text}`}>{total > 0 ? Math.round((p.count / total) * 100) : 0}%</span>
                  </div>
                </div>
              )
            }} />
            <Bar dataKey="count" fill={OTHER} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// ── Screen per pass ──────────────────────────────────────────────────────────
// Every pass that runs the sourcing screen (a sourcing run over its new rows,
// the backfill job, an analyse run over its selection) as four columns:
// signals (what the pass obtained before the screen), screened (how many of
// those it looked at), kept (what it let stand), impersonation (the pass's
// rows whose verdict is brand impersonation as of now -- a pass analysed
// later grows it; only sourcing passes carry the run id that links rows to
// a pass, the others draw no red column). The operator's colours
// (2026-10-17): yellow, gray, blue, red. Reads /auto-domains/screen-passes,
// which reduces job_run.result (backend/queries/autoDomains.ts
// screenPassFromRun).

const SIGNALS = '#B8860B'
const SCREENED = '#808693'
const KEPT = 'var(--signal)'
const LURE_PASS = '#CC3D3D'
const SCREEN_PASSES = 30

const fmtPassTick = (iso: string) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
}
const fmtPassLong = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
const pctText = (v: number | null) => (v == null ? '—' : `${v}%`)

export function ScreenFunnelCard({
  platformIdsKey,
  refreshToken,
  isDark,
  muted,
  text,
  className = 'h-full',
}: {
  /** comma-joined platform ids, as the dashboard's other fetches use */
  platformIdsKey: string
  /** anything that changes when the dashboard refreshes (its lastUpdated) */
  refreshToken?: unknown
  isDark: boolean
  muted: string
  text: string
  className?: string
}) {
  const [passes, setPasses] = React.useState<AutoDomainScreenPass[] | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let alive = true
    apiGet<AutoDomainScreenPass[]>(`/auto-domains/screen-passes?${platformIdsKey ? `platform_ids=${platformIdsKey}&` : ''}limit=${SCREEN_PASSES}`).then((r) => {
      if (!alive) return
      if (r.success && r.data) { setPasses(r.data); setError(null) } else { setError(r.error ?? 'screen passes unavailable') }
    })
    return () => { alive = false }
  }, [platformIdsKey, refreshToken])

  const data = (passes ?? []).map((p) => ({ ...p, label: p.started_at ?? p.run_id }))
  const totals = data.reduce((t, p) => ({ signals: t.signals + p.signals, screened: t.screened + p.screened, kept: t.kept + p.kept, lures: t.lures + (p.impersonation ?? 0) }), { signals: 0, screened: 0, kept: 0, lures: 0 })
  const keptPct = totals.screened > 0 ? Math.round((100 * totals.kept) / totals.screened) : null
  const last = data.length > 0 ? data[data.length - 1] : null
  const tickInterval = Math.max(0, Math.ceil(data.length / 10) - 1)
  const legendDot = (colour: string, label: string) => (
    <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full" style={{ background: colour }} />{label}</span>
  )

  return (
    <div className={`${cardCls(isDark)} ${className}`}>
      <div className="flex shrink-0 items-start justify-between gap-4 px-4 pt-3 pb-2">
        <div className="min-w-0">
          <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Screen per pass · signals → screened → kept</div>
          <div className={`mt-0.5 text-base font-semibold tracking-tight ${text}`}>
            {data.length > 0 ? <>{keptPct ?? '—'}% kept of {totals.screened.toLocaleString()} screened · <span className={'text-destructive'}>{totals.lures}</span> impersonations</> : 'No screened passes yet'}
          </div>
          {last && (
            <div className={`mt-0.5 font-mono text-[10px] ${muted}`}>
              last {data.length} passes · latest {fmtPassLong(last.started_at)} ({last.job}): {last.signals.toLocaleString()} signals, {last.screened} screened, <span className={'text-signal'}>{last.kept}</span> kept ({pctText(last.kept_pct)})
              {last.impersonation != null && <>, <span className={'text-destructive'}>{last.impersonation}</span> impersonation{last.impersonation === 1 ? '' : 's'} so far</>}
              {last.discarded > 0 && <> · dropped {last.infra} infra, {last.unrelated} unrelated</>}
            </div>
          )}
          {error && <div className={`mt-0.5 font-mono text-[10px] text-destructive`}>{error}</div>}
        </div>
        <div className={`flex min-w-0 shrink items-center justify-end gap-3 font-mono text-[9px] ${muted}`}>
          {legendDot(SIGNALS, 'Signals')}
          {legendDot(SCREENED, 'Screened')}
          {legendDot(KEPT, 'Kept')}
          {legendDot(LURE_PASS, 'Impersonation')}
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {data.length === 0 ? (
          <div className={`flex h-full items-center justify-center font-mono text-[11px] ${muted}`}>
            {passes === null && !error ? 'Loading…' : 'No pass has run the screen for this scope'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: -24, bottom: 4 }} barCategoryGap="25%" barGap={2}>
              <XAxis dataKey="label" tick={tick(isDark)} tickLine={false} axisLine={false} interval={tickInterval} tickFormatter={fmtPassTick} />
              <YAxis tick={tick(isDark)} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: 'var(--secondary)' }} content={({ active, payload }: any) => {
                if (!active || !payload?.length) return null
                const p = payload[0].payload as AutoDomainScreenPass
                const row = (colour: string, label: string, value: React.ReactNode) => (
                  <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full" style={{ background: colour }} /><span>{label}</span><span className={`ml-auto pl-4 font-semibold ${text}`}>{value}</span></div>
                )
                return (
                  <div className={tooltipCls(isDark)}>
                    <div className={`mb-1 font-semibold ${muted}`}>{fmtPassLong(p.started_at)} · {p.job}{p.trigger ? ` · ${p.trigger}` : ''}</div>
                    {row(SIGNALS, 'Signals', p.signals.toLocaleString())}
                    {row(SCREENED, 'Screened', <>{p.screened} <span className={muted}>({pctText(p.screened_pct)})</span></>)}
                    {row(KEPT, 'Kept', <>{p.kept} <span className={muted}>({pctText(p.kept_pct)})</span></>)}
                    {p.impersonation != null && row(LURE_PASS, 'Impersonation', <>{p.impersonation} <span className={muted}>({p.kept > 0 ? `${Math.round((1000 * p.impersonation) / p.kept) / 10}% of kept` : 'as of now'})</span></>)}
                    <div className={`mt-1 flex items-center justify-between gap-6 ${muted}`}><span>Dropped</span><span className={`font-semibold ${text}`}>{p.discarded}<span className={`font-normal ${muted}`}> · {p.infra} infra, {p.unrelated} unrelated</span></span></div>
                    {p.new_rows != null && <div className={`flex items-center justify-between gap-6 ${muted}`}><span>New rows</span><span className={`font-semibold ${text}`}>{p.new_rows}</span></div>}
                  </div>
                )
              }} />
              <Bar dataKey="signals" fill={SIGNALS} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
              <Bar dataKey="screened" fill={SCREENED} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
              <Bar dataKey="kept" fill={KEPT} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
              <Bar dataKey="impersonation" fill={LURE_PASS} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
